import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, BookOpen, CheckCircle2, Folder, Layers } from 'lucide-react'
import { useCatalogCourse } from '../hooks/useCatalog'
import CEFRBadge from '../components/shared/CEFRBadge'
// TODO: re-enable Pro gate before launch (restore ProBadge import and isPro check)
import type { CatalogDeck, CatalogFolder } from '../types'
import { useLocale } from '../i18n/context'
import { libraryHref } from '../utils/libraryAddress'

// A catalog course is only ever opened from Catalog › Courses, and with no
// saved return address, so that is where leaving it goes — not the Catalog's
// first sub-tab, which is where the bare `/catalog` redirect lands.
const BACK_DEST = libraryHref('catalog', 'courses')

export default function CatalogCourseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { tr } = useLocale()
  const cat = tr.catalog

  const { data: course, isLoading, isError } = useCatalogCourse(id!)

  if (isLoading) return <LoadingState />
  if (isError || !course) return <ErrorState onBack={() => navigate(BACK_DEST)} />

  const allFolderDeckIds = new Set(course.folders.flatMap((f) => f.decks.map((d) => d.id)))
  const looseDecks = course.decks.filter((d) => !allFolderDeckIds.has(d.id))

  // Preview cards: first 5 from the first deck found
  const firstDeck = course.folders[0]?.decks[0] ?? course.decks[0]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 32, maxWidth: 860, margin: '0 auto' }}>
      {/* ── Back ────────────────────────────────────────────────────────── */}
      <Link
        to={BACK_DEST}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)', textDecoration: 'none', fontSize: 14, fontWeight: 500 }}
      >
        <ArrowLeft size={16} />
        {cat.title}
      </Link>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{
          width: 64, height: 64, borderRadius: 16, flexShrink: 0,
          backgroundColor: 'var(--accent-soft)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <BookOpen size={30} color="var(--accent)" />
        </div>

        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
              {course.title}
            </h1>
            <CEFRBadge level={course.cefr_level} size="md" />
            {course.is_enrolled && (
              <span style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '3px 10px', borderRadius: 50,
                backgroundColor: 'rgba(39,174,96,0.12)', color: 'var(--success)',
                fontSize: 12, fontWeight: 600,
              }}>
                <CheckCircle2 size={13} />
                {cat.enrolled}
              </span>
            )}
          </div>

          {course.description && (
            <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              {course.description}
            </p>
          )}

          {/* Stats row */}
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {[
              { icon: <Folder size={14} />,  label: cat.deckCount(course.deck_count) },
              { icon: <BookOpen size={14} />, label: cat.cardCount(course.card_count) },
              { icon: <Layers size={14} />,   label: `${course.topic_count} ${cat.topics}` },
            ].map(({ icon, label }) => (
              <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--text-secondary)' }}>
                <span style={{ color: 'var(--accent)' }}>{icon}</span>
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* Enroll CTA — TODO: re-enable Pro gate before launch */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
          {!course.is_enrolled ? (
            <EnrollButton courseId={course.id} />
          ) : (
            <UnenrollButton courseId={course.id} />
          )}
        </div>
      </div>

      {/* ── Content tree ────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
          {cat.courseContent}
        </h2>

        {/* Folders */}
        {course.folders.map((folder) => (
          <FolderSection key={folder.id} folder={folder} />
        ))}

        {/* Loose decks (not in any folder) */}
        {looseDecks.map((deck) => (
          <DeckRow key={deck.id} deck={deck} />
        ))}

        {course.folders.length === 0 && looseDecks.length === 0 && (
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{cat.noContent}</p>
        )}
      </div>

      {/* ── Card preview ────────────────────────────────────────────────── */}
      {firstDeck && (
        <div>
          <h2 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
            {cat.sampleCards(firstDeck.title)}
          </h2>
          <PreviewPlaceholders count={5} />
        </div>
      )}
    </div>
  )
}

// ── Enroll / Unenroll (stubs — API endpoints 202 async) ──────────────────────

function EnrollButton({ courseId: _courseId }: { courseId: string }) {
  const { tr } = useLocale()
  return (
    <button
      onClick={() => {
        // TODO: POST /api/v1/catalog/courses/{id}/enroll (202 async)
        alert('Enroll — coming soon')
      }}
      style={{
        padding: '12px 28px', borderRadius: 50, border: 'none',
        backgroundColor: 'var(--accent-fill)', color: 'var(--text-on-accent)',
        fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-ui)',
        transition: 'opacity 0.15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85' }}
      onMouseLeave={(e) => { e.currentTarget.style.opacity = '1' }}
    >
      {tr.catalog.enrollBtn}
    </button>
  )
}

function UnenrollButton({ courseId: _courseId }: { courseId: string }) {
  const { tr } = useLocale()
  return (
    <button
      onClick={() => {
        // TODO: DELETE /api/v1/catalog/courses/{id}/enroll
        alert('Unenroll — coming soon')
      }}
      style={{
        padding: '11px 28px', borderRadius: 50,
        border: '1.5px solid var(--border)', backgroundColor: 'transparent',
        color: 'var(--text-secondary)',
        fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)',
        transition: 'border-color 0.15s, color 0.15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--danger)'; e.currentTarget.style.color = 'var(--danger)' }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)' }}
    >
      {tr.catalog.unenrollBtn}
    </button>
  )
}

// ── Folder section ────────────────────────────────────────────────────────────

function FolderSection({ folder }: { folder: CatalogFolder }) {
  const { tr } = useLocale()
  return (
    <div style={{
      backgroundColor: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 16,
      overflow: 'hidden',
      boxShadow: 'var(--shadow-card)',
    }}>
      {/* Folder header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '14px 20px',
        borderBottom: folder.decks.length > 0 ? '1px solid var(--border)' : 'none',
        backgroundColor: 'var(--bg-secondary)',
      }}>
        <Folder size={16} color="var(--accent)" />
        <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>{folder.title}</span>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-secondary)' }}>
          {tr.catalog.deckCount(folder.decks.length)}
        </span>
      </div>

      {/* Decks */}
      {folder.decks.map((deck, i) => (
        <DeckRow key={deck.id} deck={deck} noBorder={i === folder.decks.length - 1} indent />
      ))}
    </div>
  )
}

function DeckRow({ deck, noBorder, indent }: { deck: CatalogDeck; noBorder?: boolean; indent?: boolean }) {
  const { tr } = useLocale()
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: indent ? '12px 20px 12px 44px' : '12px 20px',
      borderBottom: noBorder ? 'none' : '1px solid var(--border)',
      backgroundColor: indent ? 'var(--bg-surface)' : 'var(--bg-surface)',
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: 8, flexShrink: 0,
        backgroundColor: 'var(--accent-soft)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <BookOpen size={15} color="var(--accent)" />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {deck.title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {deck.topic && (
            <span style={{
              padding: '1px 8px', borderRadius: 50,
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-secondary)', fontSize: 11, fontWeight: 500,
            }}>
              {deck.topic}
            </span>
          )}
          <CEFRBadge level={deck.cefr_level} />
        </div>
      </div>

      <span style={{ fontSize: 12, color: 'var(--text-secondary)', flexShrink: 0 }}>
        {tr.catalog.cardCount(deck.card_count)}
      </span>
    </div>
  )
}

// ── Card preview placeholders ─────────────────────────────────────────────────

function PreviewPlaceholders({ count }: { count: number }) {
  const { tr } = useLocale()
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            padding: '14px 18px',
            backgroundColor: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            display: 'flex',
            gap: 16,
            alignItems: 'center',
          }}
        >
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            backgroundColor: 'var(--accent-soft)',
            flexShrink: 0,
          }} />
          {/* Shimmer bars simulating card content */}
          <div style={{ flex: 1, display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{
              height: 14, borderRadius: 6,
              backgroundColor: 'var(--bg-secondary)',
              width: `${30 + (i * 17) % 25}%`,
            }} />
            <div style={{ color: 'var(--border)', fontSize: 16, userSelect: 'none' }}>→</div>
            <div style={{
              height: 14, borderRadius: 6,
              backgroundColor: 'var(--bg-secondary)',
              width: `${35 + (i * 11) % 30}%`,
            }} />
          </div>
        </div>
      ))}
      <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center' }}>
        {tr.catalog.enrollToSee}
      </p>
    </div>
  )
}

// ── Loading / Error states ────────────────────────────────────────────────────

function LoadingState() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 860, margin: '0 auto' }}>
      {[200, 400, 300].map((_, i) => (
        <div key={i} style={{ height: i === 0 ? 20 : i === 1 ? 80 : 60, borderRadius: 12, backgroundColor: 'var(--bg-secondary)' }} />
      ))}
    </div>
  )
}

function ErrorState({ onBack }: { onBack: () => void }) {
  const { tr } = useLocale()
  const cat = tr.catalog
  return (
    <div style={{ textAlign: 'center', padding: '80px 0' }}>
      <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 6px' }}>
        {cat.courseNotFound}
      </p>
      <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: '0 0 20px' }}>
        {cat.courseRemovedDesc}
      </p>
      <button
        onClick={onBack}
        style={{
          padding: '10px 20px', borderRadius: 50, border: 'none',
          backgroundColor: 'var(--accent-fill)', color: 'var(--text-on-accent)',
          fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)',
        }}
      >
        {cat.backToCatalog}
      </button>
    </div>
  )
}
